import { IbdFirstInteractionEvent, IbdEvents } from './_ibd-events.js';

/**
 * Deep merges two objects, recursively merging nested objects.
 * Arrays and primitives from the source object will replace those in the target.
 *
 * @param {any} target - The target object to merge into
 * @param {any} source - The source object to merge from
 * @returns {any} The merged object
 */
export function deepMerge(target, source) {
  if (source === null || source === undefined) {
    return target;
  }

  if (typeof source !== 'object' || Array.isArray(source)) {
    return source;
  }

  if (typeof target !== 'object' || Array.isArray(target)) {
    return source;
  }

  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      if (
        typeof source[key] === 'object' &&
        source[key] !== null &&
        !Array.isArray(source[key]) &&
        typeof target[key] === 'object' &&
        target[key] !== null &&
        !Array.isArray(target[key])
      ) {
        result[key] = deepMerge(target[key], source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }

  return result;
}

export class IbdSkeleton extends HTMLElement {
  shimmer() {
    this.setAttribute('ibd-shimmer', '');
  }

  resetShimmer() {
    this.removeAttribute('ibd-shimmer');
  }
}

if (!customElements.get('ibd-skeleton')) {
  customElements.define('ibd-skeleton', IbdSkeleton);
}

/**
 * Resets the ibd-shimmer attribute on all elements in the container.
 * @param {Element} [container] - The container to reset the shimmer attribute on.
 */
export function resetIbdSkeleton(container = document.body) {
  const ibdSkeleton = container.querySelectorAll('ibd-skeleton');
  ibdSkeleton.forEach((item) => item.removeAttribute('ibd-shimmer'));
}

/**
 * Stores the first interaction event detail once it has been triggered.
 * This allows late listeners to execute immediately if the event already occurred.
 * @type {IbdFirstInteractionEvent | null}
 */
let firstInteractionEvent = null;

/**
 * Tracks whether detection has been initialized to prevent multiple initializations.
 * @type {boolean}
 */
let detectionInitialized = false;

/**
 * Detects the first user interaction and dispatches a custom event.
 * Listens for click, scroll, keydown, touchstart, and mousemove events.
 * Once triggered, dispatches 'ibd:first-interaction' event and removes all listeners.
 *
 * @param {Object} [options] - Configuration options
 * @param {boolean} [options.includeMouseMove=true] - Whether to include mousemove as a trigger
 * @param {boolean} [options.once=true] - Whether to only trigger once (default: true)
 * @returns {Function} A cleanup function that removes all event listeners
 *
 * @example
 * // Initialize detection (only needs to be called once per page)
 * detectFirstInteraction();
 */
export function detectFirstInteraction(options = {}) {
  const { includeMouseMove = true, once = true } = options;

  // If already initialized, don't initialize again
  // (even if event hasn't fired yet, we don't want duplicate listeners)
  if (detectionInitialized) {
    return () => {}; // Return no-op cleanup function
  }

  // If already triggered, don't initialize again
  if (once && firstInteractionEvent !== null) {
    return () => {}; // Return no-op cleanup function
  }

  detectionInitialized = true;

  let hasTriggered = false;
  /** @type {AddEventListenerOptions} */
  const eventOptions = { passive: true, once: once };
  /** @type {AddEventListenerOptions} */
  const scrollOptions = { capture: true, once: once };

  /**
   * @param {Event} event
   * @param {string} interactionType
   */
  function handleInteraction(event, interactionType) {
    if (once && hasTriggered) return;

    hasTriggered = true;

    // Create and store the custom event
    const customEvent = new IbdFirstInteractionEvent(event, interactionType);
    firstInteractionEvent = customEvent;

    document.dispatchEvent(customEvent);

    if (once) {
      cleanup();
    }
  }

  function cleanup() {
    document.removeEventListener('click', clickHandler, eventOptions);
    document.removeEventListener('scroll', scrollHandler, { capture: true });
    document.removeEventListener('keydown', keydownHandler, eventOptions);
    document.removeEventListener('touchstart', touchstartHandler, eventOptions);
    if (includeMouseMove) {
      document.removeEventListener('mousemove', mousemoveHandler, eventOptions);
    }
  }

  // Create specific handlers for each event type
  /** @param {MouseEvent} e */
  const clickHandler = (e) => handleInteraction(e, 'click');
  /** @param {Event} e */
  const scrollHandler = (e) => handleInteraction(e, 'scroll');
  /** @param {KeyboardEvent} e */
  const keydownHandler = (e) => handleInteraction(e, 'keydown');
  /** @param {TouchEvent} e */
  const touchstartHandler = (e) => handleInteraction(e, 'touchstart');
  /** @param {MouseEvent} e */
  const mousemoveHandler = (e) => handleInteraction(e, 'mousemove');

  // Set up event listeners
  // Using capture phase for scroll to catch all scroll events
  document.addEventListener('click', clickHandler, eventOptions);
  // TypeScript doesn't recognize passive for Document scroll, but it's valid in browsers
  document.addEventListener('scroll', scrollHandler, /** @type {any} */ (scrollOptions));
  document.addEventListener('keydown', keydownHandler, eventOptions);
  document.addEventListener('touchstart', touchstartHandler, eventOptions);
  if (includeMouseMove) {
    // Fallback: load after mouse movement (indicates user is active)
    document.addEventListener('mousemove', mousemoveHandler, eventOptions);
  }

  return cleanup;
}

/**
 * Listens for the first user interaction event.
 * If the event has already occurred, executes the handler immediately.
 * This ensures that late listeners don't miss the first interaction.
 *
 * @param {Function} handler - The callback function to execute when first interaction occurs
 * @param {AddEventListenerOptions} [options] - Event listener options (e.g., { once: true })
 * @returns {Function} A cleanup function that removes the event listener
 *
 * @example
 * // Listen for first interaction (works even if called after interaction occurred)
 * onFirstInteraction((event) => {
 *   console.log('User interacted!', event.detail.interactionType);
 *   loadScript();
 * });
 */
/**
 * @param {(event: IbdFirstInteractionEvent) => void} handler
 * @param {AddEventListenerOptions} [options]
 * @returns {() => void}
 */
export function onFirstInteraction(handler, options = {}) {
  // If the event has already occurred, execute handler immediately
  if (firstInteractionEvent !== null) {
    handler(firstInteractionEvent);
    return () => {}; // Return no-op cleanup function
  }

  // Otherwise, add event listener
  // Create wrapper function to maintain reference for cleanup
  const wrappedHandler = /** @type {EventListener} */ (
    (event) => {
      handler(/** @type {IbdFirstInteractionEvent} */ (event));
    }
  );

  document.addEventListener(IbdEvents.ibdFirstInteraction, wrappedHandler, options);

  // Return cleanup function
  // Note: removeEventListener is safe to call even if listener was auto-removed by once: true
  return () => {
    document.removeEventListener(IbdEvents.ibdFirstInteraction, wrappedHandler, options);
  };
}
